import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'idsk-react-components';
import { getTitle, setTitle } from 'slovlex-idsk-react-components';
import { useSuccessMessageContext } from '../../context/SuccessMessageContext';
import { useClient, useClientStatusContext } from '../../context/ClientStatusContext';
import PrispevokVestnika from '../../model/PrispevokVestnika';
import useFieldErrorMessages from '../../hooks/useFieldErrorMessages';
import { addPrispevokVestnika, validatePrispevokVestnika } from '../../client/prispevokVestnikaAddClient';
import { SafeParameterSetter } from '../../model/types/SafeParameterSetter';
import PrispevokVestnikaStepEnum from '../../model/PrispevokVestnikaStepEnum';
import routes from '../../routing/routes';
import BasicDataStep1 from './stepPages/BasicDataStep1/BasicDataStep1Create';
import DatesStep2 from './stepPages/DatesStep2';
import RelatedDataStep3 from './stepPages/RelatedDataStep3';
import Organizacia from '../../model/ciselniky/Organizacia';
import AttachmentsStep4 from './stepPages/AttachmentsStep4';
import './stepPages/stepPages.scss';
import MainAttachmentStep5 from './stepPages/MainAttachmentStep5';
import CheckDataStep6 from './stepPages/CheckDataStep6';
import scrollToTop from '../../function/scrollToTop';

const PrispevokVestnikaCreate = (): ReactElement => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    clearStatus,
  } = useClientStatusContext();

  const [prispevokVestnika, setPrispevokVestnika] = useState<PrispevokVestnika>({
    vestnik: {} as Organizacia,
    cislo: '',
    nazov: '',
    kratkyNazov: '',
    datumUcinnosti: null,
    datumZverejnenia: null,
    popis: '',
    meniPrispevky: [],
    rusiPrispevky: [],
    vykonavaciePredpisy: [],
    prilohy: [],
    activeStep: PrispevokVestnikaStepEnum.BASIC_DATA_1,
  });

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
    clearArrayFieldErrorMessage,
  } = useFieldErrorMessages();
  const {
    wrapClient: validatePrispevokVestnikaClient,
    status: validatePrispevokVestnikaStatus,
  } = useClient(validatePrispevokVestnika);
  const {
    wrapClient: createPrispevokVestnikaClient,
    status: createPrispevokVestnikaStatus,
  } = useClient(addPrispevokVestnika);

  const updateField: SafeParameterSetter<PrispevokVestnika> = (fieldName, value) => {
    setPrispevokVestnika(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const onChange = async (
    nextStep: PrispevokVestnikaStepEnum,
  ): Promise<void> => {
    updateField('activeStep', nextStep);
    scrollToTop();
  };

  const handleSubmit = async (e, nextStep): Promise<void> => {
    e.preventDefault();
    switch (prispevokVestnika.activeStep) {
      case PrispevokVestnikaStepEnum.BASIC_DATA_1:
      case PrispevokVestnikaStepEnum.DATES_2:
      case PrispevokVestnikaStepEnum.RELATED_DATA_3:
      case PrispevokVestnikaStepEnum.ATTACHMENTS_4:
      case PrispevokVestnikaStepEnum.MAIN_ATTACHMENT_5:
        await validatePrispevokVestnikaClient(prispevokVestnika);
        updateField('activeStep', nextStep);
        scrollToTop();
        return;
      case PrispevokVestnikaStepEnum.CHECK_DATA_6:
        await createPrispevokVestnikaClient(prispevokVestnika).then(response => {
          navigate(routes.prispevokVestnika.detail.withParams(response.data));
          scrollToTop();
        });
        displaySuccess('Príspevok vestníka bol úspešne odoslaný do úložiska.');
        return;
      default:
        updateField('activeStep', nextStep);
        scrollToTop();
    }
  };

  const renderActiveStepPage = (): ReactElement => {
    switch (prispevokVestnika.activeStep) {
      case PrispevokVestnikaStepEnum.DATES_2:
        return (
          <DatesStep2
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case PrispevokVestnikaStepEnum.RELATED_DATA_3:
        return (
          <RelatedDataStep3
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case PrispevokVestnikaStepEnum.ATTACHMENTS_4:
        return (
          <AttachmentsStep4
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
            clearArrayFieldErrorMessage={clearArrayFieldErrorMessage}
            clearFieldErrorMessage={clearFieldErrorMessage}
          />
        );
      case PrispevokVestnikaStepEnum.MAIN_ATTACHMENT_5:
        return (
          <MainAttachmentStep5
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case PrispevokVestnikaStepEnum.CHECK_DATA_6:
        return (
          <CheckDataStep6
            onChange={onChange}
            clearStatus={clearStatus}
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={createPrispevokVestnikaStatus.loading}
          />
        );
      default:
        return (
          <BasicDataStep1
            prispevokVestnika={prispevokVestnika}
            onSubmit={handleSubmit}
            onUpdateField={updateField}
            loading={validatePrispevokVestnikaStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
    }
  };

  useEffect(() => {
    setTitle(getTitle());
  }, [prispevokVestnika.activeStep]);

  return (
    <Row>
      <Col widthDesktop="three-quarters">
        {renderActiveStepPage()}
      </Col>
    </Row>
  );
};

export default PrispevokVestnikaCreate;
